/**
 * Desktop mega menu — contextual: each nav link with children (Marcas,
 * Hombres, Suplementos, whichever actually has children) has its own
 * panel inside a shared shell; only one panel shows at a time. Not a
 * <dialog> deliberately — it's non-modal by design (a light scrim, never
 * blocking, page content still usable behind it), and native <dialog>
 * only gets Escape-to-close and a real backdrop through showModal(),
 * which also traps focus and would fight that "not blocking" requirement.
 * So Escape, click-outside and hover-intent are all wired by hand here.
 */
if (!customElements.get('mega-menu')) {
  customElements.define(
    'mega-menu',
    class MegaMenu extends HTMLElement {
      connectedCallback() {
        this.shell = this.querySelector('.mega-menu');
        this.scrim = this.querySelector('.mega-menu__scrim');
        if (!this.shell) return;

        this.activeKey = null;
        this.closeTimer = null;
        this.panels = new Map();

        this.querySelectorAll('[data-mega-panel]').forEach((panel) => {
          const key = panel.dataset.megaPanel;
          const trigger = document.querySelector(`[data-mega-trigger="${key}"]`);
          if (!trigger) return;

          this.panels.set(key, { panel, trigger });

          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggle(key);
          });
          trigger.addEventListener('mouseenter', () => this.scheduleOpen(key));
          trigger.addEventListener('focus', () => this.open(key));
          trigger.addEventListener('mouseleave', () => this.scheduleClose());
          panel.addEventListener('mouseenter', () => this.cancelClose());
          panel.addEventListener('mouseleave', () => this.scheduleClose());
        });

        this.scrim?.addEventListener('click', () => this.closeAll());

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.activeKey) this.closeAll();
        });

        // Click or keyboard focus landing anywhere outside this element
        // (AND outside every trigger — the triggers live in the header
        // bar, a sibling of <mega-menu>, not a descendant, so `this.contains`
        // alone would treat clicking/focusing the trigger that just opened
        // a panel as an "outside" interaction and immediately close it
        // again) closes it. Navigating to a link inside the panel is a
        // click on a descendant of `this`, so it's correctly excluded
        // too — the page just navigates away.
        const isOutside = (target) =>
          !this.contains(target) && ![...this.panels.values()].some(({ trigger }) => trigger.contains(target));

        document.addEventListener('click', (event) => {
          if (!this.activeKey || !isOutside(event.target)) return;
          this.closeAll();
        });
        document.addEventListener('focusin', (event) => {
          if (!this.activeKey || !isOutside(event.target)) return;
          this.closeAll();
        });

        window.addEventListener('resize', () => {
          if (this.activeKey && !window.matchMedia('(min-width: 1024px)').matches) this.closeAll();
        });
      }

      scheduleOpen(key) {
        clearTimeout(this.closeTimer);
        this.open(key);
      }

      scheduleClose() {
        clearTimeout(this.closeTimer);
        // Short enough to feel immediate, long enough that moving the
        // cursor from the trigger down into the panel doesn't clip
        // through a gap and close it prematurely.
        this.closeTimer = setTimeout(() => this.closeAll(), 150);
      }

      cancelClose() {
        clearTimeout(this.closeTimer);
      }

      open(key) {
        if (!this.panels.has(key) || this.activeKey === key) return;
        document.querySelector('predictive-search')?.close?.();
        if (this.activeKey) this.setPanelVisible(this.activeKey, false);
        this.setPanelVisible(key, true);
        this.activeKey = key;
        this.syncPosition();
        this.shell.hidden = false;
        if (this.scrim) this.scrim.hidden = false;
        if (!this.boundSyncPosition) this.boundSyncPosition = () => this.syncPosition();
        window.addEventListener('scroll', this.boundSyncPosition, { passive: true });
        window.addEventListener('resize', this.boundSyncPosition);
        this.dispatchEvent(new CustomEvent('mega-menu:open', { bubbles: true }));
      }

      // top: var(--header-height) alone is wrong the moment an announcement
      // bar sits above the header — that CSS var only ever measures the
      // header bar's OWN height, never whatever real content the sticky
      // header currently has above it in the viewport (the announcement
      // bar, before the user scrolls past it). Measuring the header's live
      // rendered bottom edge here, every time the panel opens or the page
      // scrolls/resizes while it's open, is the only way to stay correct
      // in both the "announcement bar still showing" and "scrolled past
      // it, header stuck at the very top" states.
      syncPosition() {
        const header = this.closest('.site-header') || document.querySelector('.site-header');
        if (!header) return;
        const top = `${Math.round(header.getBoundingClientRect().bottom)}px`;
        this.shell.style.top = top;
        if (this.scrim) this.scrim.style.top = top;
      }

      toggle(key) {
        if (this.activeKey === key) {
          this.closeAll();
        } else {
          this.open(key);
        }
      }

      closeAll() {
        clearTimeout(this.closeTimer);
        if (!this.activeKey) return;
        this.setPanelVisible(this.activeKey, false);
        this.activeKey = null;
        this.shell.hidden = true;
        if (this.scrim) this.scrim.hidden = true;
        if (this.boundSyncPosition) {
          window.removeEventListener('scroll', this.boundSyncPosition);
          window.removeEventListener('resize', this.boundSyncPosition);
        }
        this.dispatchEvent(new CustomEvent('mega-menu:close', { bubbles: true }));
      }

      setPanelVisible(key, visible) {
        const entry = this.panels.get(key);
        if (!entry) return;
        entry.panel.hidden = !visible;
        entry.trigger.setAttribute('aria-expanded', visible ? 'true' : 'false');
      }
    }
  );
}
